import { AUTH_TOKEN_URL } from './constants';
import { AuthStorage, AuthTokenPair } from './types';

interface RefreshTokenResponse {
	access_token: string;
	refresh_token: string;
}

export class AuthClient {
	private refreshPromise: Promise<AuthTokenPair> | null = null;

	constructor(private storage: AuthStorage) {}

	async getAccessToken() {
		const tokens = await this.storage.getSessionTokens();

		return tokens?.accessToken ?? null;
	}

	async refreshToken() {
		if (this.refreshPromise) {
			return this.refreshPromise;
		}

		this.refreshPromise = this.performRefresh();

		try {
			return await this.refreshPromise;
		} finally {
			this.refreshPromise = null;
		}
	}

	private async performRefresh() {
		const tokens = await this.storage.getSessionTokens();

		if (!tokens?.sessionToken) {
			throw new Error('No refresh token');
		}

		const newTokens = await refreshTokens(tokens.sessionToken);

		await this.storage.setSessionTokens(newTokens);

		return newTokens;
	}

	async clearTokens() {
		await this.storage.clearSessionTokens();
	}
}

const refreshTokens = async (sessionToken: string): Promise<AuthTokenPair> => {
	const response = await fetch(AUTH_TOKEN_URL, {
		method: 'POST',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify({
			grant_type: 'refresh_token',
			refresh_token: sessionToken,
		}),
	});

	if (!response.ok) {
		throw new Error('Session token expired');
	}

	let data: RefreshTokenResponse;
	try {
		data = (await response.json()) as RefreshTokenResponse;
	} catch {
		throw new Error('Invalid token response');
	}
	if (!data) {
		throw new Error('Could not load data');
	}
	return {
		accessToken: data.access_token,
		sessionToken: data.refresh_token,
	};
};
